import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import SaakhSetuLoader from '@/components/SaakhSetuLoader';
import MultiStepForm from '@/components/FarmerForm/MultiStepForm';
import Step1Personal from '@/components/FarmerForm/Step1Personal';
import Step2Location from '@/components/FarmerForm/Step2Location';
import Step3Agricultural from '@/components/FarmerForm/Step3Agricultural';
import Step4Financial from '@/components/FarmerForm/Step4Financial';
import Step5Documents from '@/components/FarmerForm/Step5Documents';
import Step6CreditHistory from '@/components/FarmerForm/Step6CreditHistory';
import Step7OptionalFields from '@/components/FarmerForm/Step7OptionalFields';
import Step8ExternalData from '@/components/FarmerForm/Step8ExternalData';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  { title: 'Personal', description: 'Basic information and contact details' },
  { title: 'Location', description: 'Address and location details' },
  { title: 'Agricultural', description: 'Land and crop information' },
  { title: 'Financial', description: 'Income and financial details' },
  { title: 'Credit History', description: 'Credit and repayment history' },
  { title: 'Optional Fields', description: 'Additional information (optional)' },
  { title: 'Documents', description: 'Upload supporting documents' },
  { title: 'External Data', description: 'Fetch external integrations (optional)' }
];

const FarmerFormNew = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const returnJourneyType = searchParams.get('journeyType');
  const isEdit = !!id;

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [errors, setErrors] = useState({});
  const [formSaved, setFormSaved] = useState(false);
  const [createdFarmerId, setCreatedFarmerId] = useState(null);

  useEffect(() => {
    if (isEdit) {
      fetchFarmer();
      fetchDocuments();
    }
  }, [id]);

  const fetchFarmer = async () => {
    const farmerId = id || createdFarmerId;
    if (!farmerId) return;
    setFetchingData(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/farmers/${farmerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const farmerData = response.data;
      
      // Convert location NAMES to IDs for cascading dropdowns
      let locationIds = {
        state_id: null,
        district_id: null,
        taluk_id: null,
        village_id: null
      };
      
      // Convert crop NAME to category for cascading dropdown
      let cropData = {
        primary_crop_category: null,
        primary_crop_type: farmerData.primary_crop_type
      };
      
      try {
        // Fetch master data to map names to IDs
        const masterDataResponse = await axios.get(`${API}/master-data/states`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const states = masterDataResponse.data.states || masterDataResponse.data;
        
        // Find state ID by name — try residential_* first (new form), fallback to land_*
        const stateName = farmerData.residential_state || farmerData.land_state;
        if (stateName) {
          const state = states.find(s => s.name === stateName);
          if (state) {
            locationIds.state_id = state.id;
            
            // Fetch districts for this state
            const districtResponse = await axios.get(`${API}/master-data/districts/${state.id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const districts = districtResponse.data.districts || districtResponse.data;
            
            // Find district ID by name
            const districtName = farmerData.residential_district || farmerData.land_district;
            if (districtName) {
              const district = districts.find(d => d.name === districtName);
              if (district) {
                locationIds.district_id = district.id;
                
                // Fetch taluks for this district
                const talukResponse = await axios.get(`${API}/master-data/taluks/${district.id}`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                const taluks = talukResponse.data.taluks || talukResponse.data;
                
                // Find taluk ID by name
                const talukName = farmerData.residential_taluk || farmerData.land_taluk;
                if (talukName) {
                  const taluk = taluks.find(t => t.name === talukName);
                  if (taluk) {
                    locationIds.taluk_id = taluk.id;
                    
                    // Village name is used directly (not ID-based in the dropdown)
                    locationIds.village_id = farmerData.residential_village || farmerData.land_village;
                  }
                }
              }
            }
          }
        }
        
        // Fetch crop master data to find category
        if (farmerData.primary_crop_type) {
          const cropsResponse = await axios.get(`${API}/master-data/crops`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const allCrops = cropsResponse.data.crops || [];
          
          // Find the crop by name to get its category
          const cropMatch = allCrops.find(c => c.name === farmerData.primary_crop_type);
          if (cropMatch) {
            cropData.primary_crop_category = cropMatch.category;
          }
        }
      } catch (error) {
        console.error('Error mapping data for edit mode:', error);
        // Continue with farmer data even if mapping fails
      }
      
      // Map backend field names to form field names for edit mode
      const mappedData = {
        ...farmerData,
        // Location IDs for cascading dropdowns
        state_id: locationIds.state_id || farmerData.state_id,
        district_id: locationIds.district_id || farmerData.district_id,
        taluk_id: locationIds.taluk_id || farmerData.taluk_id,
        village_id: locationIds.village_id || farmerData.village_id,
        // Keep the actual land_* fields for display/submission
        land_state: farmerData.residential_state || farmerData.land_state,
        land_district: farmerData.residential_district || farmerData.land_district,
        land_taluk: farmerData.residential_taluk || farmerData.land_taluk,
        land_village: farmerData.residential_village || farmerData.land_village,
        residential_state: farmerData.residential_state || farmerData.land_state,
        residential_district: farmerData.residential_district || farmerData.land_district,
        residential_taluk: farmerData.residential_taluk || farmerData.land_taluk,
        residential_village: farmerData.residential_village || farmerData.land_village,
        // Crop category for cascading dropdown
        primary_crop_category: cropData.primary_crop_category,
        primary_crop_type: cropData.primary_crop_type,
        // Map backend field names to frontend field names for optional fields
        equipment_value: farmerData.equipment_mechanization_value || farmerData.equipment_value,
        financial_literacy_score: farmerData.financial_literacy_score || 
                                   (farmerData.financial_literacy_level ? 
                                    (farmerData.financial_literacy_level === 'High' ? 75 : 
                                     farmerData.financial_literacy_level === 'Medium' ? 50 : 25) : null),
        technology_adoption_score: farmerData.technology_adoption_score,
        training_program_participation: farmerData.training_program_participation,
        value_chain_integration: farmerData.value_chain_integration || 
                                 (farmerData.value_chain_integration_level ? 
                                  (farmerData.value_chain_integration_level === 'High' ? 8 : 
                                   farmerData.value_chain_integration_level === 'Medium' ? 5 : 
                                   farmerData.value_chain_integration_level === 'Low' ? 2 : 0) : null),
      };
      
      setFormData(mappedData);
    } catch (error) {
      toast.error('Failed to load farmer data');
      // Only redirect if this was the initial edit load, not a refresh
      if (!formSaved && !createdFarmerId) {
        navigate('/dashboard');
      }
    } finally {
      setFetchingData(false);
    }
  };

  const fetchDocuments = async () => {
    const farmerId = id || createdFarmerId;
    if (!farmerId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/farmers/${farmerId}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to load documents', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    console.log('Validating step:', step, 'with formData:', formData);

    if (step === 0) {
      // Step 1: Personal Information
      if (!formData.farmer_name?.trim()) newErrors.farmer_name = 'Farmer name is required';
      if (!formData.date_of_birth) newErrors.date_of_birth = 'Date of birth is required';
      else if (formData.age && (formData.age < 18 || formData.age > 100)) newErrors.date_of_birth = 'Age must be between 18-100 years';
      if (!formData.contact_number?.trim()) newErrors.contact_number = 'Contact number is required';
      else if (formData.contact_number.length !== 10) newErrors.contact_number = 'Contact number must be 10 digits';
      if (!formData.aadhaar_number?.trim()) newErrors.aadhaar_number = 'Aadhaar number is required';
      else if (formData.aadhaar_number.length !== 12) newErrors.aadhaar_number = 'Aadhaar must be exactly 12 digits';
      if (!formData.farming_experience_years && formData.farming_experience_years !== 0) newErrors.farming_experience_years = 'Farming experience is required';
    } else if (step === 1) {
      // Step 2: Location (Farmer Residential Location)
      if (!formData.residential_state) newErrors.residential_state = 'State is required';
      if (!formData.residential_district) newErrors.residential_district = 'District is required';
      // Taluk and Village are optional for residential location
    } else if (step === 2) {
      // Step 3: Agricultural - NEW VALIDATION
      // Land area unit is required
      if (!formData.land_area_unit) newErrors.land_area_unit = 'Measurement unit is required';
      
      // Land parcels validation (at least 1 parcel with complete data)
      if (!formData.land_parcels || formData.land_parcels.length === 0) {
        newErrors.land_parcels = 'At least one land parcel is required';
      } else {
        // Validate each parcel has required fields based on new structure
        const unit = formData.land_area_unit;
        const invalidParcels = formData.land_parcels.filter(p => {
          // Check area based on unit
          let hasArea = false;
          if (unit === 'Hectares') {
            hasArea = p.area && parseFloat(p.area) > 0;
          } else if (unit === 'Acres & Guntas' || unit === 'Acres & Cents') {
            hasArea = p.area_acres && parseFloat(p.area_acres) > 0;
          }
          
          // Check location fields
          const hasLocation = p.state && p.district && p.taluk && p.village;
          const hasDistance = p.distance_to_market_km && parseFloat(p.distance_to_market_km) >= 0;
          
          // Check parcel characteristics
          const hasOwnership = p.ownership_status;
          const hasIrrigation = p.irrigation_status;
          const hasSoilType = p.soil_type;
          const hasSoilTexture = p.soil_texture;
          
          return !(hasArea && hasLocation && hasDistance && hasOwnership && hasIrrigation && hasSoilType && hasSoilTexture);
        });
        
        if (invalidParcels.length > 0) {
          newErrors.land_parcels = 'All parcels must have complete information (area, location, distance to market, ownership, irrigation, soil type & texture)';
        }
      }
      
      // Crops validation (at least 1 crop)
      if (!formData.crops || formData.crops.length === 0) {
        newErrors.crops = 'At least one crop is required';
      } else {
        // Validate crops have required fields
        const invalidCrops = formData.crops.filter(c => 
          !c.linked_parcel_id || !c.category || !c.crop_type || !c.year || !c.season || !c.area || parseFloat(c.area) <= 0
        );
        if (invalidCrops.length > 0) {
          newErrors.crops = 'All crops must have parcel, category, type, year, season, and area';
        }
        
        // Validate yield history for each crop
        const cropsWithIncompleteYield = formData.crops.filter(c => {
          if (!c.yield_history || c.yield_history.length !== 3) return true;
          const allYieldsFilled = c.yield_history.every(y => y.yield_value && parseFloat(y.yield_value) > 0);
          return !allYieldsFilled;
        });
        
        if (cropsWithIncompleteYield.length > 0) {
          newErrors.yield_history = 'All crops must have complete 3-year yield history';
        }
      }
    } else if (step === 3) {
      // Step 4: Financial
      if (!formData.annual_agricultural_income && formData.annual_agricultural_income !== 0) newErrors.annual_agricultural_income = 'Annual income is required';
      if (!formData.total_outstanding_debt && formData.total_outstanding_debt !== 0) newErrors.total_outstanding_debt = 'Outstanding debt is required (enter 0 if none)';
      if (!formData.avg_bank_balance_6mo && formData.avg_bank_balance_6mo !== 0) newErrors.avg_bank_balance_6mo = 'Avg bank balance is required';
      if (!formData.bank_account_vintage_years && formData.bank_account_vintage_years !== 0) newErrors.bank_account_vintage_years = 'Bank account age is required';
      if (formData.other_income_pct === undefined || formData.other_income_pct === '') newErrors.other_income_pct = 'Other income percentage is required';
      // FPO/SHG membership and Crop Insurance are now optional (toggle default to false if not set)
    } else if (step === 4) {
      // Step 5: Credit History
      // Phase 4: Repayment count validation
      if (!formData.full_repayment) newErrors.full_repayment = 'Full repayment status is required';
      if (!formData.prior_default_history) newErrors.prior_default_history = 'Prior default history is required';
    }
    // Step 5 (Optional Fields) and Step 6 (Documents) have no required validation

    console.log('Validation errors for step', step, ':', newErrors);
    console.log('Validation result:', Object.keys(newErrors).length === 0);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < STEPS.length - 1) {
        setCurrentStep(prev => prev + 1);
      }
    } else {
      toast.error('Please fill all required fields');
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Prepare payload with only backend-expected fields
      const payload = {
        // Personal (5 mandatory)
        farmer_name: formData.farmer_name,
        date_of_birth: formData.date_of_birth,
        age: formData.age,
        contact_number: formData.contact_number,
        aadhaar_number: formData.aadhaar_number,
        farming_experience_years: formData.farming_experience_years,
        
        // Residential Location (Step 2)
        residential_state: formData.residential_state,
        residential_district: formData.residential_district,
        ...(formData.residential_taluk && { residential_taluk: formData.residential_taluk }),
        ...(formData.residential_village && { residential_village: formData.residential_village }),
        
        // Old location fields - kept for backward compatibility
        ...(formData.land_state && { land_state: formData.land_state }),
        ...(formData.land_district && { land_district: formData.land_district }),
        ...(formData.land_taluk && { land_taluk: formData.land_taluk }),
        ...(formData.land_village && { land_village: formData.land_village }),
        ...(formData.distance_to_market_km && { distance_to_market_km: formData.distance_to_market_km }),
        
        // Agricultural - NEW STRUCTURE
        // Land parcels and calculated fields
        land_area_unit: formData.land_area_unit,
        land_parcels: formData.land_parcels || [],
        total_land_holding_size_acres: formData.total_land_holding_size_acres,
        land_area_hectares: formData.land_area_hectares,
        ...(formData.land_fragmentation_index && { land_fragmentation_index: formData.land_fragmentation_index }),
        
        // Crops and calculated fields
        crops: formData.crops || [],
        primary_crop_type: formData.primary_crop_type,
        
        // Phase 3: Year-wise yield
        ...(formData.crop_yield_year_1 && { crop_yield_year_1: formData.crop_yield_year_1 }),
        ...(formData.crop_yield_year_2 && { crop_yield_year_2: formData.crop_yield_year_2 }),
        ...(formData.crop_yield_year_3 && { crop_yield_year_3: formData.crop_yield_year_3 }),
        crop_yield_history_3yr_avg: formData.crop_yield_history_3yr_avg,
        
        crop_yield_variance: formData.crop_yield_variance,
        crop_diversification_index: formData.crop_diversification_index,
        crop_diversification_index: formData.crop_diversification_index,
        
        // Financial (7 mandatory - removed pm_kisan_beneficiary_status)
        annual_agricultural_income: formData.annual_agricultural_income,
        other_income_pct: formData.other_income_pct ?? 0,
        total_outstanding_debt: formData.total_outstanding_debt,
        avg_bank_balance_6mo: formData.avg_bank_balance_6mo,
        bank_account_vintage_years: formData.bank_account_vintage_years,
        
        // Credit History (2 mandatory + Phase 4 count)
        ...(formData.full_repayment && { full_repayment: formData.full_repayment }),
        repayment_track_record_pct: formData.repayment_track_record_pct,
        prior_default_history: formData.prior_default_history,
        
        // Government Schemes (2 mandatory - removed pm_kisan_beneficiary_status)
        fpo_shg_membership: formData.fpo_shg_membership || false,
        crop_insurance_status: formData.crop_insurance_status || false,
        
        // Market Integration (optional)
        ...(formData.sales_channel && { sales_channel: formData.sales_channel }),
        ...(formData.value_addition && { value_addition: formData.value_addition }),
        
        // Yogyank-mapped financial fields
        ...(formData.banking_transactions_count && { banking_transactions_count: formData.banking_transactions_count }),
        ...(formData.kcc_transaction_frequency && { kcc_transaction_frequency: formData.kcc_transaction_frequency }),
        ...(formData.annual_income_retention_ratio_pct && { annual_income_retention_ratio_pct: formData.annual_income_retention_ratio_pct }),
        
        // Optional fields (if present) - credit_score removed from form (Phase 5)
        ...(formData.livestock_value && { livestock_value: formData.livestock_value }),
        ...(formData.equipment_mechanization_value && { equipment_mechanization_value: formData.equipment_mechanization_value }),
        ...(formData.education_level && { education_level: formData.education_level }),
        ...(formData.financial_literacy_level && { financial_literacy_level: formData.financial_literacy_level }),
        ...(formData.family_size && { family_size: formData.family_size }),
        ...(formData.family_support_score && { family_support_score: formData.family_support_score }),
        ...(formData.contract_farming_status !== undefined && { contract_farming_status: formData.contract_farming_status }),
        ...(formData.ndvi_trend_score && { ndvi_trend_score: formData.ndvi_trend_score }),
        ...(formData.rainfall_deviation_pct && { rainfall_deviation_pct: formData.rainfall_deviation_pct }),
        ...(formData.soil_health_metrics && { soil_health_metrics: formData.soil_health_metrics }),
        ...(formData.geographic_vulnerability_index && { geographic_vulnerability_index: formData.geographic_vulnerability_index }),
        ...(formData.debt_to_income_ratio && { debt_to_income_ratio: formData.debt_to_income_ratio }),
        ...(formData.input_cost_ratio && { input_cost_ratio: formData.input_cost_ratio }),
        ...(formData.credit_drawdown_ratio_pct && { credit_drawdown_ratio_pct: formData.credit_drawdown_ratio_pct }),
        ...(formData.digital_transaction_activity && { digital_transaction_activity: formData.digital_transaction_activity }),
        ...(formData.govt_scheme_participation_count && { govt_scheme_participation_count: formData.govt_scheme_participation_count }),
        ...(formData.access_to_extension_services !== undefined && { access_to_extension_services: formData.access_to_extension_services }),
        ...(formData.kcc_renewal_history && { kcc_renewal_history: formData.kcc_renewal_history }),
        ...(formData.land_fragmentation_index && { land_fragmentation_index: formData.land_fragmentation_index }),
        ...(formData.collateral_value_estimated && { collateral_value_estimated: formData.collateral_value_estimated }),
        ...(formData.existing_loan_type && { existing_loan_type: formData.existing_loan_type }),
        ...(formData.num_active_loans && { num_active_loans: formData.num_active_loans }),
        ...(formData.months_since_last_loan && { months_since_last_loan: formData.months_since_last_loan })
      };

      if (isEdit) {
        await axios.put(`${API}/farmers/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Farmer profile updated successfully!');
        fetchFarmer(); // Refresh to get updated credit score
      } else {
        const response = await axios.post(`${API}/farmers`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const newFarmerId = response.data.id;
        setCreatedFarmerId(newFarmerId);
        setFormSaved(true);
        toast.success('Farmer profile created! You can now upload documents.');
        // Stay on step 6 (Documents) — farmerId is now available for uploads
      }
    } catch (error) {
      // Handle FastAPI validation errors which return array of error objects
      let errorMessage = 'Failed to save farmer profile';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          // Extract readable messages from validation error array
          errorMessage = detail.map(err => {
            const field = err.loc ? err.loc.join('.') : 'unknown';
            return `${field}: ${err.msg}`;
          }).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }
      
      toast.error(errorMessage);
      console.error('Save error:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentsUpdate = () => {
    const farmerId = id || createdFarmerId;
    if (!farmerId) return;
    fetchDocuments();
    // Only refresh farmer data if we have a valid ID; catch silently
    const token = localStorage.getItem('token');
    axios.get(`${API}/farmers/${farmerId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(response => {
      const farmerData = response.data;
      setFormData(prev => ({
        ...prev,
        yogyank_score_final: farmerData.yogyank_score_final,
        yogyank_score_band: farmerData.yogyank_score_band,
      }));
    }).catch(() => {
      // Silent - don't redirect on refresh failure
    });
  };

  const handleExternalDataFetched = (integrationType, data) => {
    // Optional: Show toast notification
    toast.success(`${integrationType} data fetched successfully!`);
  };

  const handleFinishStep8 = () => {
    const farmerId = id || createdFarmerId;
    if (returnTo === 'assessment' && farmerId) {
      // Return to assessment workflow with the farmer ready to select
      const jt = returnJourneyType || 'NEW_FARMER';
      navigate(`/assessment/workflow/${jt}?farmerId=${farmerId}`);
      toast.success('Farmer profile completed! Continue with assessment.');
    } else {
      navigate('/dashboard');
      toast.success('Farmer profile completed successfully!');
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <Step1Personal formData={formData} onChange={handleChange} errors={errors} />;
      case 1:
        return <Step2Location formData={formData} onChange={handleChange} errors={errors} />;
      case 2:
        return <Step3Agricultural formData={formData} onChange={handleChange} errors={errors} />;
      case 3:
        return <Step4Financial formData={formData} onChange={handleChange} errors={errors} />;
      case 4:
        return <Step6CreditHistory formData={formData} onChange={handleChange} errors={errors} />;
      case 5:
        return <Step7OptionalFields formData={formData} onChange={handleChange} />;
      case 6:
        return <Step5Documents farmerId={id || createdFarmerId} documents={documents} onDocumentsUpdate={handleDocumentsUpdate} />;
      case 7:
        return <Step8ExternalData 
          farmerId={id || createdFarmerId} 
          farmerData={formData}
          currentUser={user}
          onDataFetched={handleExternalDataFetched}
        />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
      <nav className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-button">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            </div>
            <img src="/saakhsetu-logo.png" alt="SaakhSetu" style={{ height: '28px' }} />
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {fetchingData ? (
          <div className="flex items-center justify-center py-24">
            <SaakhSetuLoader message="Loading farmer profile..." size="lg" />
          </div>
        ) : (
        <>
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#1a202c' }}>
            {isEdit ? 'Edit Farmer Profile' : 'Create New Farmer Profile'}
          </h2>
          <p className="text-gray-600">{isEdit ? 'Edit farmer information across 8 steps' : 'Complete all steps to create comprehensive farmer profile'}</p>

          {/* Score Readiness Indicator */}
          {(() => {
            const mandatoryFilled = ['farmer_name', 'date_of_birth', 'contact_number', 'aadhaar_number',
              'farming_experience_years', 'residential_state', 'residential_district',
              'annual_agricultural_income', 'total_outstanding_debt', 'avg_bank_balance_6mo',
              'bank_account_vintage_years', 'prior_default_history'].filter(f => formData[f] != null && formData[f] !== '' && formData[f] !== 0);
            const total = 13;
            const pct = Math.round((mandatoryFilled.length / total) * 100);
            const isReady = pct === 100 && (formData.land_parcels?.length > 0);
            return (
              <Card className={`mt-3 ${isReady ? 'border-green-200 bg-green-50/50' : 'border-gray-200'}`} data-testid="readiness-indicator">
                <CardContent className="py-2.5 px-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isReady ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <AlertCircle className="h-4 w-4 text-gray-400" />}
                    <span className="text-xs font-medium text-gray-700">Score Readiness</span>
                    <Badge className={`text-[10px] ${isReady ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {isReady ? 'READY' : `${pct}% complete`}
                    </Badge>
                  </div>
                  <span className="text-[10px] text-gray-400">{mandatoryFilled.length}/{total} mandatory fields</span>
                </CardContent>
              </Card>
            );
          })()}
        </div>

        <MultiStepForm steps={STEPS} currentStep={currentStep}>
          {renderStep()}
        </MultiStepForm>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6">
          {/* Back Button - Disable on Step 8 in create mode only when NOT yet saved */}
          {!(currentStep === 7 && !isEdit && !formSaved) && (
            <Button
              type="button"
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 0}
              data-testid="back-button"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          )}

          <div className="flex space-x-3 ml-auto">
            {currentStep === 7 ? (
              // Step 8: External Data - Show "Skip & Finish" or "Finish"
              <Button
                type="button"
                onClick={handleFinishStep8}
                data-testid="finish-button"
                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
              >
                {formData.weather_data || formData.satellite_data || formData.pm_kisan_data || formData.soil_health_data 
                  ? 'Finish' 
                  : 'Skip & Finish'}
              </Button>
            ) : currentStep < STEPS.length - 1 ? (
              currentStep === 6 && !isEdit && !formSaved ? (
                // Step 7 (Documents) in create mode before save - Show "Submit"
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  data-testid="save-farmer-button"
                  style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <img src="/saakhsetu-loader.webp" alt="" style={{ width: 18, height: 18 }} />
                      Saving...
                    </span>
                  ) : 'Submit'}
                </Button>
              ) : (
                // Regular Next button (also for step 6 after save)
                <Button
                  type="button"
                  onClick={handleNext}
                  data-testid="next-button"
                  style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              )
            ) : (
              // Edit mode - Update Profile button
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                data-testid="save-farmer-button"
                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <img src="/saakhsetu-loader.webp" alt="" style={{ width: 18, height: 18 }} />
                    Saving...
                  </span>
                ) : 'Update Profile'}
              </Button>
            )}
          </div>
        </div>
        </>
        )}
      </main>
    </div>
  );
};

export default FarmerFormNew;
